import React from 'react';
import { SubType } from '@erecruit/ui-kit/src/utils/tsHelpers';
import {
  CompanyDepartmentsFilter,
  Requests,
  Resources,
  Mutations,
  AppPermissions,
} from '@erecruit/types';
import { Intl } from '@erecruit/core';
import { Text, ProgressStatus, ProgressStatusProps } from '@erecruit/ui-kit';
import {
  ColumnFilters,
  TableDateRangeFilter,
  TableTimeRangeFilter,
  OnDateRangeFilter,
  ResetDateFilter,
  OnTimeRangeFilter,
  TableColumn,
  Departments,
  ThemedLink,
  Authorize,
} from '@erecruit/modules';

import { PositionSearchFilterData } from './state/reducer';
import { ApprovalPathStatuses } from '../ApprovalPath';

const { FormattedMessage, FormattedDate } = Intl;

const messages = messageIds.vms.positionSearch;
const approvalPathMessages = messageIds.vms.approvalPath;

type SearchParamsKey = keyof SubType<
  Resources.PositionSearchFilter,
  number[] | CompanyDepartmentsFilter[]
>;

export type PositionColumnType = TableColumn<
  Resources.PositionSearch,
  number | CompanyDepartmentsFilter,
  Resources.PositionSearchFilter,
  number[] | CompanyDepartmentsFilter[]
>;

export const defaultVisibleColumns = [
  nameof<Resources.PositionSearch>(o => o.name),
  nameof.full<Resources.PositionSearch>(o => o.positionType.name),
  nameof.full<Resources.PositionSearch>(o => o.company.name),
  nameof<Resources.PositionSearch>(o => o.createdOn),
  nameof<Resources.PositionSearch>(o => o.estimatedStartDate),
  nameof<Resources.PositionSearch>(o => o.estimatedEndDate),
  nameof.full<Resources.PositionSearch>(o => o.positionStatus.name),
];

export const getDefinedPositionReportColumns = (
  intl: Intl.InjectedIntl,
): Mutations.ColumnDefinition[] => {
  return [
    {
      id: nameof<Resources.PositionSearch>(o => o.name),
      header: intl.formatMessage({ id: messages.name }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.createdOn),
      header: intl.formatMessage({ id: messages.createdOn }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.contact),
      header: intl.formatMessage({ id: messages.contact }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.companyDepartments),
      header: intl.formatMessage({ id: messages.companyDepartments }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.positionType),
      header: intl.formatMessage({ id: messages.positionType }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.positionStatus),
      header: intl.formatMessage({ id: messages.positionStatus }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.company),
      header: intl.formatMessage({ id: messages.company }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.worksite),
      header: intl.formatMessage({ id: messages.worksite }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.estimatedStartDate),
      header: intl.formatMessage({ id: messages.estimatedStartDate }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.estimatedEndDate),
      header: intl.formatMessage({ id: messages.estimatedEndDate }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.startTime),
      header: intl.formatMessage({ id: messages.startTime }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.endTime),
      header: intl.formatMessage({ id: messages.endTime }),
    },
    {
      id: nameof<Resources.PositionSearch>(o => o.approvalPath),
      header: intl.formatMessage({ id: messages.approvalPath }),
    },
  ];
};

export const renderApprovalPathColumn = (
  approvalPath: Resources.ApprovalPathOverview,
) => {
  if (!approvalPath) {
    return '-';
  }

  let title = <FormattedMessage id={approvalPathMessages.status.inProgress} />;
  let status: ProgressStatusProps['status'] = 'pending';

  if (approvalPath.approvalStatus === Resources.ApprovalStatus.Approved) {
    title = <FormattedMessage id={approvalPathMessages.status.approved} />;
    status = 'success';
  }

  if (approvalPath.approvalStatus === Resources.ApprovalStatus.Rejected) {
    title = <FormattedMessage id={approvalPathMessages.status.rejected} />;
    status = 'critical';
  }

  return (
    <ProgressStatus
      status={status}
      title={title}
      count={approvalPath.currentStepNumber}
      sum={approvalPath.totalSteps}
    />
  );
};

const filterTransform = (values: string[]) => values.map(Number);

type ParseTableFilters = (
  filters: Partial<Record<keyof Resources.PositionSearch, string[]>>,
  columns: PositionColumnType[],
) => Resources.PositionSearchFilter;

export const parseTableFilters: ParseTableFilters = (filters, columns) => {
  const filter: Resources.PositionSearchFilter = {};
  Object.keys(filters).forEach(key => {
    const filterColumnKey = key as keyof Resources.PositionSearch;
    const column = columns.find(col => col.key === key);

    if (column?.searchParamsKey && column?.filterTransform) {
      const { filterTransform, searchParamsKey } = column;

      if (searchParamsKey === 'companies') {
        filter.companies = filterTransform(
          filters[filterColumnKey] || [],
        ) as CompanyDepartmentsFilter[];
      } else {
        filter[searchParamsKey] = filterTransform(
          filters[filterColumnKey] || [],
        ) as number[];
      }
    } else {
      console.warn(`Table filter ${key} applied but can't parsed.`);
    }
  });
  return filter;
};

type GenerateColumns = (
  filterByDate: OnDateRangeFilter,
  filterByTime: OnTimeRangeFilter,
  resetDateFilter: ResetDateFilter,
  filterState: Resources.PositionSearchFilter,
  searchQuery: string,
  filterData?: PositionSearchFilterData,
) => PositionColumnType[];

const getPositionColumn = ({
  link,
  searchQuery,
}: {
  link?: boolean;
  searchQuery?: string;
}): PositionColumnType => ({
  title: <FormattedMessage id={messages.name} />,
  key: nameof<Resources.PositionSearch>(o => o.name),
  sorter: true,
  fixed: 'left',
  render: (value, { name, id, company }) => (
    <>
      <Authorize
        permission={`${AppPermissions.ViewPositions} & ${AppPermissions.ViewPosition}`}
      >
        {(isAuthorized: boolean) =>
          isAuthorized && link ? (
            <ThemedLink to={`/positions/${id}${searchQuery}`}>
              {`${name} (${id})`}
            </ThemedLink>
          ) : (
            <Text>
              {name} ({id})
            </Text>
          )
        }
      </Authorize>
      <Text>{company?.name}</Text>
    </>
  ),
});

const getPositionCreatedOnColumn = (
  filterState: Resources.PositionSearchFilter,
  resetDateFilter: ResetDateFilter,
  filterByDate: OnDateRangeFilter,
): PositionColumnType => ({
  title: <FormattedMessage id={messages.createdOn} />,
  dataIndex: nameof<Resources.PositionSearch>(o => o.createdOn),
  key: nameof<Resources.PositionSearch>(o => o.createdOn),
  sorter: true,
  filteredValue: filterState.createdOn
    ? [filterState.createdOn.from, filterState.createdOn.to]
    : [],
  render: value => (value ? <FormattedDate value={value} /> : null),
  filterDropdown: ({ confirm }) => (
    <TableDateRangeFilter
      dateFieldKey="createdOn"
      range={filterState.createdOn}
      resetFilter={(dateFieldKey: string) => {
        confirm && confirm();
        resetDateFilter(dateFieldKey);
      }}
      onFilter={(dateFieldKey, dateRange) => {
        confirm && confirm();
        filterByDate(dateFieldKey, dateRange);
      }}
    />
  ),
});

const getPositionContactColumn = (
  filterState: Resources.PositionSearchFilter,
): PositionColumnType => ({
  title: <FormattedMessage id={messages.contact} />,
  dataIndex: nameof.full<Resources.PositionSearch>(o => o.contact.name),
  key: nameof.full<Resources.PositionSearch>(o => o.contact.name),
  sorter: true,
  searchParamsKey: nameof<Resources.PositionSearchFilter>(
    o => o.contactIds,
  ) as keyof SearchParamsKey,
  filteredValue: filterState.contactIds || [],
  filterDropdown: props => (
    <ColumnFilters {...props} request={Requests.ContactRequest} />
  ),
  filterTransform,
});

const getPositionDepartmentColumn = (
  filterState: Resources.PositionSearchFilter,
): PositionColumnType => ({
  title: <FormattedMessage id={messages.companyDepartments} />,
  key: nameof<Resources.PositionSearch>(o => o.companyDepartments),
  searchParamsKey: nameof<Resources.PositionSearchFilter>(
    o => o.companyDepartmentIds,
  ) as keyof SearchParamsKey,
  filteredValue: filterState.companyDepartmentIds || [],
  filterDropdown: props => (
    <ColumnFilters {...props} request={Requests.FacilityDepartmentRequest} />
  ),
  filterTransform,
  render: ({
    companyDepartments,
  }: {
    companyDepartments: Resources.PositionSearch['companyDepartments'];
  }) => {
    return <Departments departments={companyDepartments} />;
  },
});

export const positionColumns = {
  getPositionColumn,
  getPositionCreatedOnColumn,
  getPositionContactColumn,
  getPositionDepartmentColumn,
};

export const generateColumns: GenerateColumns = (
  filterByDate,
  filterByTime,
  resetDateFilter,
  filterState,
  searchQuery,
) => {
  const columns: PositionColumnType[] = [
    {
      title: <FormattedMessage id={messages.positionType} />,
      dataIndex: nameof.full<Resources.PositionSearch>(
        o => o.positionType.name,
      ),
      key: nameof.full<Resources.PositionSearch>(o => o.positionType.name),
      sorter: true,
      searchParamsKey: nameof<Resources.PositionSearchFilter>(
        o => o.positionTypeIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.positionTypeIds || [],
      filterDropdown: props => (
        <ColumnFilters
          {...props}
          request={Requests.PositionTypeRequest}
          getParamsFromSettings={appSettings => ({
            ids: appSettings?.dataPreferences.positionTypes,
          })}
        />
      ),
      filterTransform,
    },
    {
      title: <FormattedMessage id={messages.positionStatus} />,
      dataIndex: nameof.full<Resources.PositionSearch>(
        o => o.positionStatus.name,
      ),
      key: nameof.full<Resources.PositionSearch>(o => o.positionStatus.name),
      sorter: true,
      searchParamsKey: nameof<Resources.PositionSearchFilter>(
        o => o.positionStatusIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.positionStatusIds || [],
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.PositionStatusRequest} />
      ),
      filterTransform,
    },
    {
      title: <FormattedMessage id={messages.company} />,
      dataIndex: nameof.full<Resources.PositionSearch>(o => o.company.name),
      key: nameof.full<Resources.PositionSearch>(o => o.company.name),
      sorter: true,
      searchParamsKey: nameof<Resources.PositionSearchFilter>(
        o => o.companies,
      ) as keyof SearchParamsKey,
      filteredValue:
        filterState.companies?.map(company => company.companyId) || [],
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.CompanyRequest} />
      ),
      filterTransform: values => {
        return values.map(value => ({
          companyId: Number(value),
          departmentIds: [],
        })) as CompanyDepartmentsFilter[];
      },
    },
    {
      title: <FormattedMessage id={messages.worksite} />,
      dataIndex: nameof.full<Resources.PositionSearch>(o => o.worksite.name),
      key: nameof.full<Resources.PositionSearch>(o => o.worksite.name),
      sorter: true,
      searchParamsKey: nameof<Resources.PositionSearchFilter>(
        o => o.worksiteIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.worksiteIds || [],
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.WorksiteRequest} />
      ),
      filterTransform,
      permission: AppPermissions.ViewPositionLocation,
    },
    {
      title: <FormattedMessage id={messages.estimatedStartDate} />,
      dataIndex: nameof<Resources.PositionSearch>(o => o.estimatedStartDate),
      key: nameof<Resources.PositionSearch>(o => o.estimatedStartDate),
      sorter: true,
      filteredValue: filterState.estimatedStartDate
        ? [
            filterState.estimatedStartDate.from,
            filterState.estimatedStartDate.to,
          ]
        : [],
      render: value => (value ? <FormattedDate value={value} /> : '-'),
      filterDropdown: ({ confirm }) => (
        <TableDateRangeFilter
          dateFieldKey={nameof<Resources.PositionSearch>(
            o => o.estimatedStartDate,
          )}
          range={filterState.estimatedStartDate}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, dateRange) => {
            confirm && confirm();
            filterByDate(dateFieldKey, dateRange);
          }}
        />
      ),
      permission: AppPermissions.ViewPositionSchedule,
    },
    {
      title: <FormattedMessage id={messages.estimatedEndDate} />,
      dataIndex: nameof<Resources.PositionSearch>(o => o.estimatedEndDate),
      key: nameof<Resources.PositionSearch>(o => o.estimatedEndDate),
      sorter: true,
      filteredValue: filterState.estimatedEndDate
        ? [filterState.estimatedEndDate.from, filterState.estimatedEndDate.to]
        : [],
      render: value => (value ? <FormattedDate value={value} /> : '-'),
      filterDropdown: ({ confirm }) => (
        <TableDateRangeFilter
          dateFieldKey={nameof<Resources.PositionSearch>(
            o => o.estimatedEndDate,
          )}
          range={filterState.estimatedEndDate}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, dateRange) => {
            confirm && confirm();
            filterByDate(dateFieldKey, dateRange);
          }}
        />
      ),
      permission: AppPermissions.ViewPositionSchedule,
    },
    {
      title: <FormattedMessage id={messages.startTime} />,
      dataIndex: nameof<Resources.PositionSearch>(o => o.startTime),
      key: nameof<Resources.PositionSearch>(o => o.startTime),
      sorter: true,
      filteredValue: filterState.startTime
        ? [filterState.startTime.from, filterState.startTime.to]
        : [],
      filterDropdown: ({ confirm }) => (
        <TableTimeRangeFilter
          dateFieldKey={nameof<Resources.PositionSearch>(o => o.startTime)}
          range={filterState.startTime}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, timeRange) => {
            confirm && confirm();
            filterByTime(dateFieldKey, timeRange);
          }}
        />
      ),
    },
    {
      title: <FormattedMessage id={messages.endTime} />,
      dataIndex: nameof<Resources.PositionSearch>(o => o.endTime),
      key: nameof<Resources.PositionSearch>(o => o.endTime),
      sorter: true,
      filteredValue: filterState.endTime
        ? [filterState.endTime.from, filterState.endTime.to]
        : [],
      filterDropdown: ({ confirm }) => (
        <TableTimeRangeFilter
          dateFieldKey={nameof<Resources.PositionSearch>(o => o.endTime)}
          range={filterState.endTime}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, timeRange) => {
            confirm && confirm();
            filterByTime(dateFieldKey, timeRange);
          }}
        />
      ),
    },
    {
      title: <FormattedMessage id={messages.approvalPath} />,
      dataIndex: nameof<Resources.PositionSearch>(o => o.approvalPath),
      key: nameof<Resources.PositionSearch>(o => o.approvalPath),
      filteredValue: filterState.approvalStatuses || [],
      filters: Object.keys(Resources.ApprovalStatus)
        .filter(key => !isNaN(Number(key)))
        .map(key => Number(key))
        .map(approvalStatus => ({
          text: (
            <FormattedMessage
              id={
                ApprovalPathStatuses[approvalStatus as Resources.ApprovalStatus]
              }
            />
          ),
          value: String(approvalStatus),
        })),
      filterTransform: filterTransform,
      searchParamsKey: nameof<Resources.PositionSearchFilter>(
        o => o.approvalStatuses,
      ) as keyof SearchParamsKey,
      render: (value, { approvalPath }: Resources.PositionSearch) =>
        renderApprovalPathColumn(approvalPath),
    },
  ];

  columns.unshift(getPositionColumn({ link: true, searchQuery }));
  columns.push(
    getPositionCreatedOnColumn(filterState, resetDateFilter, filterByDate),
  );
  columns.push(getPositionContactColumn(filterState));
  columns.push(getPositionDepartmentColumn(filterState));

  return columns;
};
